export const API_BASE = "/api/business_plan_v2";

export const API = {
	GET_PLANS: `${API_BASE}`,
	CREATE_PLAN: `${API_BASE}`,
	GET_PLAN_STORES: (planId) => `${API_BASE}/plan/${planId}/stores`,
	GET_STORE_MASTER: `${API_BASE}/store_master`,
	GET_STORE: `${API_BASE}/store`,
	CATEGORY_VIEW: `${API_BASE}/store/category_view`,
	SUBCATEGORY_VIEW: `${API_BASE}/store/subcategory_view`,
	APPROVAL: `${API_BASE}/submission/approval`,
	CREATE_SUBMISSION: `${API_BASE}/submission`,
	DUPLICATE_SUBMISSION: `${API_BASE}/submission/duplicate`,
	GET_SUBMISSION: `${API_BASE}/submission`,
	SUBMIT: `${API_BASE}/submission/submit`,
	CANCEL: `${API_BASE}/submission/cancel`,
	APPROVE: `${API_BASE}/submission/approve`,
	UNAPPROVE: `${API_BASE}/submission/unapprove`,
	BULK_UPDATE_MRP: `${API_BASE}/mrp/bulk_update`,
	ADD_MRP_ROW: `${API_BASE}/mrp/add_row`,
	SAVE_MANUAL_TARGET: `${API_BASE}/submission/save_manual_target`,
	REFRESH_HISTORICAL: `${API_BASE}/store/refresh_historical`,
};

/** BP_NSM > BP_ASM > BP_RM — mirrors backend canApproveByHierarchy */
export const BP_HIERARCHY_ROLES = ["BP_NSM", "BP_ASM", "BP_RM"];
export const BP_HIERARCHY_RANK = { BP_NSM: 3, BP_ASM: 2, BP_RM: 1 };
export const BP_PEER_APPROVER_ROLES = ["BP_NSM", "BP_ASM"];
export const BP_EDITOR_ROLES = ["BP_RM", "BP_ASM", "BP_NSM"];

const LEGACY_HIERARCHY_MAP = {
	NSM: "BP_NSM",
	ASM: "BP_ASM",
	RSM: "BP_RM",
	BP_NSM: "BP_NSM",
	BP_ASM: "BP_ASM",
	BP_RM: "BP_RM",
};

export const BP_BRAND_ROLE_TO_VALUE = {
	BP_RARE_RABBIT: "RARE RABBIT",
	BP_RARE_ONES: "RARE ONES",
	BP_RAREISM: "RAREISM",
};

export const BP_CHANNEL_ROLE_TO_VALUE = {
	BP_EBO: "EBO",
	BP_MBO: "MBO",
	BP_LFS: "LFS",
	BP_D2C: "D2C",
	BP_MARKETPLACE: "MARKETPLACE",
};

export const STORE_APPROVAL_STATUSES = ["IN PROGRESS", "SUBMITTED", "FINAL", "NOT STARTED"];
export const STORE_APPROVAL_ROLE_OPTIONS = [
	"BP_NSM",
	"BP_ASM",
	"BP_RM",
	"ADMIN",
	"BP_FINAL_APPROVER",
];

/** Prefer stored name; else capitalize email local-part (before @). */
export const displayNameFromEmail = (name, email) => {
	if (name) return name;
	if (!email || typeof email !== "string") return "User";
	const local = email.split("@")[0] || "";
	if (!local) return "User";
	return local.charAt(0).toUpperCase() + local.slice(1);
};

export const normalizeBpHierarchyRole = (roleLabel) => {
	if (!roleLabel || typeof roleLabel !== "string") return null;
	return LEGACY_HIERARCHY_MAP[roleLabel] || (BP_HIERARCHY_RANK[roleLabel] ? roleLabel : null);
};

export const resolveBpHierarchyRole = (role) => {
	if (!role) return null;
	if (typeof role === "string") return normalizeBpHierarchyRole(role);
	for (const key of BP_HIERARCHY_ROLES) {
		if (role[key] === true) return key;
	}
	return null;
};

export const canEditBp = (role) => {
	if (role?.ADMIN === true) return true;
	return BP_EDITOR_ROLES.some((key) => role?.[key] === true);
};

/** Create plan: BP_NSM and above (ADMIN, BP_FINAL_APPROVER). */
export const canCreateBpPlan = (role) => {
	if (role?.ADMIN === true || role?.BP_FINAL_APPROVER === true) return true;
	const hierarchy = resolveBpHierarchyRole(role);
	if (!hierarchy) return false;
	return BP_HIERARCHY_RANK[hierarchy] >= BP_HIERARCHY_RANK.BP_NSM;
};

export const canFinalApproveBp = (role) => role?.ADMIN === true || role?.BP_FINAL_APPROVER === true;

/** null = all (ADMIN). */
export const getAllowedBpBrands = (role) => {
	if (role?.ADMIN === true) return null;
	return Object.entries(BP_BRAND_ROLE_TO_VALUE)
		.filter(([key]) => role?.[key] === true)
		.map(([, value]) => value);
};

/** null = all (ADMIN). */
export const getAllowedBpChannels = (role) => {
	if (role?.ADMIN === true) return null;
	return Object.entries(BP_CHANNEL_ROLE_TO_VALUE)
		.filter(([key]) => role?.[key] === true)
		.map(([, value]) => value);
};

export const canAccessBpScope = (role, brand, channel) => {
	if (role?.ADMIN === true) return true;
	const brands = getAllowedBpBrands(role);
	const channels = getAllowedBpChannels(role);
	if (!brands?.length || !channels?.length) return false;
	return brands.includes(brand) && channels.includes(channel);
};

export const canApproveByHierarchy = (actorRoleMap, creatorRoleLabel) => {
	if (actorRoleMap?.ADMIN === true) return true;

	const actorHierarchy = resolveBpHierarchyRole(actorRoleMap);
	if (!actorHierarchy || actorHierarchy === "BP_RM") return false;
	if (!BP_PEER_APPROVER_ROLES.includes(actorHierarchy)) return false;

	const creatorHierarchy =
		normalizeBpHierarchyRole(typeof creatorRoleLabel === "string" ? creatorRoleLabel : null) ||
		resolveBpHierarchyRole(creatorRoleLabel);

	if (!creatorHierarchy) return true;
	return BP_HIERARCHY_RANK[actorHierarchy] >= BP_HIERARCHY_RANK[creatorHierarchy];
};

/** Store-level status from submissions (same for every user). */
export const getStoreApprovalStatus = (store) => {
	const submissions = store?.submissions || [];
	if (!submissions.length) return "NOT STARTED";

	if (submissions.some((s) => s.is_final && s.status === "APPROVED")) return "FINAL";

	if (submissions.some((s) => s.status === "SUBMITTED")) return "SUBMITTED";

	return "IN PROGRESS";
};

/** Unique roles that have peer-approved any submission on this store. */
export const getStoreApprovalRoles = (store) => {
	const present = new Set();
	for (const sub of store?.submissions || []) {
		for (const a of sub.approvals || []) {
			if (a.user_role) present.add(a.user_role);
		}
	}
	const preferred = ["BP_NSM", "BP_ASM", "BP_RM", "NSM", "RSM", "ASM", "ADMIN", "BP_FINAL_APPROVER"];
	const ordered = preferred.filter((r) => present.has(r));
	for (const r of present) {
		if (!preferred.includes(r)) ordered.push(r);
	}
	return ordered;
};

export const brandList = [
	{
		name: "RARE RABBIT",
		key: "RARE RABBIT",
		subBrands: [
			{ name: "Rare Rabbit Mainline", key: ".", division: ["APPARELS", "ADDONS", "FOOTWEAR"] },
			{ name: "Guild", key: "GUILD", division: ["APPARELS"] },
			{ name: "Articale", key: "ARTICALE", division: ["APPARELS", "ADDONS"] },
		],
	},
	{
		name: "RARE ONES",
		key: "RARE ONES",
		subBrands: [
			{ name: "Rare Rabbit Mainline", key: ".", division: ["APPARELS", "ADDONS", "FOOTWEAR"] },
			{ name: "Guild", key: "GUILD", division: ["APPARELS"] },
			{ name: "Articale", key: "ARTICALE", division: ["APPARELS", "ADDONS"] },
		],
	},
	{
		name: "RAREISM",
		key: "RAREISM",
		subBrands: [
			{ name: "Rareism Mainline", key: ".", division: ["APPARELS", "ADDONS"] },
			{ name: "Articale", key: "ARTICALE", division: ["APPARELS", "ADDONS"] },
		],
	},
];

export const channelData = [
	{ name: "Exclusive Brand Outlets(EBO)", key: "EBO" },
	{ name: "Multi brand outlet(MBO)", key: "MBO" },
	{ name: "Large Format Store(LFS)", key: "LFS" },
	{ name: "Direct To Consumer(D2C)", key: "D2C" },
	{
		name: "Market Place",
		key: "MARKETPLACE",
		items: [
			{ name: "Myntra", key: "MYNTRA" },
			{ name: "AJIO", key: "AJIO" },
			{ name: "NYKAA", key: "NYKAA" },
			{ name: "FLIPKART", key: "FLIPKART" },
			{ name: "TATA CLIQ", key: "TATA_CLIQ" },
		],
	},
];

export const regions = [
	{ name: "North", key: "NORTH" },
	{ name: "South", key: "SOUTH" },
	{ name: "East", key: "EAST" },
	{ name: "West", key: "WEST" },
];

export const divisionData = [
	{ name: "APPARELS", key: "APPARELS" },
	{ name: "ADDONS", key: "ADDONS" },
	{ name: "FOOTWEAR", key: "FOOT WEAR" },
];

export const seasonOptions = [
	{ name: "AW", key: "AW" },
	{ name: "SS", key: "SS" },
];

export const SSMonths = [
	{ name: "Feb", key: 2 },
	{ name: "Mar", key: 3 },
	{ name: "Apr", key: 4 },
	{ name: "May", key: 5 },
	{ name: "Jun", key: 6 },
	{ name: "Jul", key: 7 },
];

export const AWMonths = [
	{ name: "Aug", key: 8 },
	{ name: "Sep", key: 9 },
	{ name: "Oct", key: 10 },
	{ name: "Nov", key: 11 },
	{ name: "Dec", key: 12 },
	{ name: "Jan", key: 1 },
];

export const getSeasonMonths = (season) => (season === "SS" ? SSMonths : AWMonths);

export const round1dp = (n) => Math.round((n + Number.EPSILON) * 10) / 10;

export const toInLakhs = (val) => round1dp((val || 0) / 100000);

export const bpTableColumns = [
	{ field: "name", headerName: "BUSINESS PLAN", flex: 1, minWidth: 110, headerAlign: "center", align: "center" },
	{ field: "brand", headerName: "BRAND", flex: 1, minWidth: 100, headerAlign: "center", align: "center" },
	{ field: "channel", headerName: "CHANNEL", flex: 1, minWidth: 90, headerAlign: "center", align: "center" },
	{ field: "total_count", headerName: "STORE COUNT", flex: 1, minWidth: 90, headerAlign: "center", align: "center" },
	{
		field: "current_count",
		headerName: "COMPLETED",
		flex: 1,
		minWidth: 90,
		headerAlign: "center",
		align: "center",
		valueGetter: (value) => value || 0,
	},
	{ field: "total_qty", headerName: "TOTAL QTY", flex: 1, minWidth: 90, headerAlign: "center", align: "center" },
	{
		field: "new_dis",
		headerName: "DISCOUNT (%)",
		flex: 1,
		minWidth: 90,
		headerAlign: "center",
		align: "center",
		valueFormatter: (value) => `${value || 0}%`,
	},
	{ field: "total_opt", headerName: "NEW OPTIONS", flex: 1, minWidth: 90, headerAlign: "center", align: "center" },
	{ field: "new_asp", headerName: "AVG SP", flex: 1, minWidth: 80, headerAlign: "center", align: "center" },
	{
		field: "new_nsv",
		headerName: "TOTAL SP (L)",
		flex: 1,
		minWidth: 90,
		headerAlign: "center",
		align: "center",
		valueGetter: (value) => round1dp((value || 0) / 100000),
	},
];

export const storeGridColumns = [
	{ field: "store_name", headerName: "STORE NAME", width: 160, minWidth: 120 },
	{ field: "mapping_code", headerName: "STORE CODE", width: 120, minWidth: 90, headerAlign: "center", align: "center" },
	{ field: "brand", headerName: "BRAND", width: 120, minWidth: 90, headerAlign: "center", align: "center" },
	{
		field: "approval_status",
		headerName: "STATUS",
		width: 130,
		minWidth: 100,
		headerAlign: "center",
		align: "center",
		type: "singleSelect",
		valueOptions: STORE_APPROVAL_STATUSES,
		filterable: true,
	},
	{
		field: "approval_roles",
		headerName: "APPROVALS",
		width: 140,
		minWidth: 100,
		headerAlign: "center",
		align: "center",
		sortable: false,
		filterable: true,
		valueGetter: (_value, row) => (row.approval_roles || []).join(", "),
	},
	{ field: "rsm_name", headerName: "RM", width: 110, minWidth: 80, headerAlign: "center", align: "center" },
	{ field: "total_qty", headerName: "TOTAL QTY", width: 100, minWidth: 80, headerAlign: "center", align: "center" },
	{
		field: "new_dis",
		headerName: "DIS%",
		width: 90,
		minWidth: 70,
		headerAlign: "center",
		align: "center",
		valueFormatter: (value) => `${value || 0}%`,
	},
	{ field: "total_opt", headerName: "NEW OPT", width: 90, minWidth: 70, headerAlign: "center", align: "center" },
	{ field: "new_asp", headerName: "ASP", width: 90, minWidth: 70, headerAlign: "center", align: "center" },
	{
		field: "new_nsv",
		headerName: "NSV (L)",
		width: 100,
		minWidth: 80,
		headerAlign: "center",
		align: "center",
		valueGetter: (value) => round1dp((value || 0) / 100000),
	},
	{ field: "store_grade", headerName: "GRADE", width: 90, minWidth: 70, headerAlign: "center", align: "center" },
	{ field: "city_tier", headerName: "TIER", width: 90, minWidth: 70, headerAlign: "center", align: "center" },
];
